import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import authService from "../services/authService";
import { isImpersonating } from "../supportMode/supportModeSession";
import {
  clearActiveMasterAccountId,
  persistActiveMasterAccountId,
} from "./activeMasterAccount";
import { fetchMeTenant } from "./tenantApi";
import { isKnownTenantKind } from "./experiences/registry";

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);

  const bootstrap = useCallback(async () => {
    const authenticated = authService.isLoggedIn() || isImpersonating();
    if (!authenticated) {
      setStatus("idle");
      setTenant(null);
      setError(null);
      return;
    }

    setStatus("loading");
    setError(null);
    try {
      const data = await fetchMeTenant();
      if (!isKnownTenantKind(data.kind)) {
        const err = new Error(`Unsupported tenant kind: ${data.kind}`);
        err.code = "UNKNOWN_KIND";
        throw err;
      }
      const token = await authService.getValidAccessToken();
      persistActiveMasterAccountId(data.masterAccountId, token);
      setTenant(data);
      setStatus("ready");
    } catch (err) {
      console.error("Tenant bootstrap failed:", err);
      setTenant(null);
      setError({
        code: err.code || "BOOTSTRAP_FAILED",
        message: err.message || "Tenant bootstrap failed",
        status: err.status,
      });
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const value = useMemo(
    () => ({
      status,
      tenant,
      error,
      isReady: status === "ready" && Boolean(tenant),
      refresh: bootstrap,
      clearTenant: () => {
        setTenant(null);
        setStatus("idle");
        setError(null);
        clearActiveMasterAccountId();
      },
    }),
    [status, tenant, error, bootstrap],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return ctx;
}

/** Safe for shared chrome that may render in tests without TenantProvider. */
export function useOptionalTenant() {
  return useContext(TenantContext);
}
